import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Filter, Download, Upload, Plus, Edit, Trash2, 
  Grid, List, Table, Eye, Mail, Phone, MapPin, Calendar, 
  Building2, UserCheck, AlertCircle, CheckCircle, X, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';
import EmployeeProfile from './EmployeeProfile';
import EmployeeImportExport from './EmployeeImportExport';
import EmployeeOnboarding from './EmployeeOnboarding';

const getSchoolKey = (school) => String(school?.key || school?.school_number || school?.id || '');

const EmployeeManagement = ({ canEdit = false }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    school_id: '',
    department: '',
    status: '',
    position_title: ''
  });
  const [viewMode, setViewMode] = useState('grid'); // grid, list, table
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showProfile, setShowProfile] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  // Schools and departments for filters
  const [schools, setSchools] = useState([]);
  const [departments] = useState([
    'Teaching Staff', 'Administration', 'Support Staff', 'Maintenance', 'Food Service'
  ]);

  useEffect(() => {
    loadEmployees();
    loadSchools();
  }, [currentPage, searchTerm, filters]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const searchFilters = {
        search_term: searchTerm,
        ...filters,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };

      const response = await apiService.makeRequest('/api/employees/search', {
        method: 'POST',
        body: JSON.stringify(searchFilters)
      });

      setEmployees(response.employees || response);
      const totalCount = response.total ?? response.employees?.length ?? 0;
      setTotalPages(totalCount > 0 ? Math.ceil(totalCount / itemsPerPage) : 0);
    } catch (error) {
      console.error('Failed to load employees:', error);
      toast({
        title: 'Error',
        description: 'Failed to load employees.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSchools = async () => {
    try {
      const schoolList = await apiService.getSchools();
      setSchools(schoolList);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadEmployees();
  };

  const handleFilterChange = (key, value) => {
    // Treat "all" as undefined/null to clear the filter
    const filterValue = value === 'all' ? undefined : value;
    setFilters(prev => ({ ...prev, [key]: filterValue }));
    setCurrentPage(1);
  };

  const handleEmployeeSelect = (employeeId, checked) => {
    setSelectedEmployees(prev => 
      checked 
        ? [...prev, employeeId]
        : prev.filter(id => id !== employeeId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedEmployees(checked ? employees.map(emp => emp.id) : []);
  };

  const handleViewEmployee = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    setShowProfile(true);
  };

  const handleBulkAction = async (action, data) => {
    if (selectedEmployees.length === 0) return;

    try {
      const operation = {
        employee_ids: selectedEmployees,
        operation: action,
        data: data
      };

      await apiService.makeRequest('/api/employees/bulk-operations', {
        method: 'POST',
        body: JSON.stringify(operation)
      });

      toast({
        title: 'Success',
        description: `Bulk operation completed for ${selectedEmployees.length} employees.`
      });

      setSelectedEmployees([]);
      setShowBulkActions(false);
      loadEmployees();
    } catch (error) {
      console.error('Bulk operation failed:', error);
      toast({
        title: 'Error',
        description: 'Bulk operation failed.',
        variant: 'destructive'
      });
    }
  };

  const exportEmployees = async () => {
    try {
      // This would typically generate and download a CSV/Excel file
      toast({
        title: 'Export Started',
        description: 'Employee data export is being prepared...'
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to export employee data.',
        variant: 'destructive'
      });
    }
  };

  if (showProfile && selectedEmployeeId) {
    return (
      <EmployeeProfile
        employeeId={selectedEmployeeId}
        onClose={() => {
          setShowProfile(false);
          setSelectedEmployeeId(null);
        }}
        canEdit={canEdit}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600">Manage employee profiles, documents, and information</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={exportEmployees}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Import/Export
              </Button>
              <Button variant="outline" onClick={() => setShowOnboarding(true)}>
                <UserCheck className="w-4 h-4 mr-2" />
                Onboard Employee
              </Button>
              <Button 
                onClick={() => setShowAddEmployee(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                Quick Add
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex space-x-2">
              <div className="flex-1">
                <Input
                  placeholder="Search employees by name, email, or position..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-black">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>School</Label>
                <Select 
                  value={filters.school_id} 
                  onValueChange={(value) => handleFilterChange('school_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All schools" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All schools</SelectItem>
                    {schools.map((school) => (
                      <SelectItem key={school.id} value={getSchoolKey(school)}>
                        {school.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Department</Label>
                <Select 
                  value={filters.department} 
                  onValueChange={(value) => handleFilterChange('department', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(value) => handleFilterChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Position</Label>
                <Input
                  placeholder="Filter by position..."
                  value={filters.position_title}
                  onChange={(e) => handleFilterChange('position_title', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Controls and Bulk Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              <Table className="w-4 h-4" />
            </Button>
          </div>
          
          <p className="text-sm text-gray-600">
            {employees.length} employees found
          </p>
        </div>

        {selectedEmployees.length > 0 && canEdit && (
          <div className="flex items-center space-x-2">
            <Badge className="bg-yellow-500 text-black">
              {selectedEmployees.length} selected
            </Badge>
            <Button
              variant="outline"
              onClick={() => setShowBulkActions(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Bulk Actions
            </Button>
          </div>
        )}
      </div>

      {/* Employee List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <>
          {viewMode === 'grid' && (
            <EmployeeGrid 
              employees={employees}
              selectedEmployees={selectedEmployees}
              onEmployeeSelect={handleEmployeeSelect}
              onViewEmployee={handleViewEmployee}
              canEdit={canEdit}
            />
          )}
          
          {viewMode === 'list' && (
            <EmployeeList 
              employees={employees}
              selectedEmployees={selectedEmployees}
              onEmployeeSelect={handleEmployeeSelect}
              onViewEmployee={handleViewEmployee}
              canEdit={canEdit}
            />
          )}
          
          {viewMode === 'table' && (
            <EmployeeTable 
              employees={employees}
              selectedEmployees={selectedEmployees}
              onEmployeeSelect={handleEmployeeSelect}
              onSelectAll={handleSelectAll}
              onViewEmployee={handleViewEmployee}
              canEdit={canEdit}
            />
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <BulkActionsDialog
        open={showBulkActions}
        onClose={() => setShowBulkActions(false)}
        selectedCount={selectedEmployees.length}
        onAction={handleBulkAction}
        schools={schools}
        departments={departments}
      />

      {showImport && (
        <EmployeeImportExport
          onClose={() => setShowImport(false)}
          onImportComplete={loadEmployees}
        />
      )}

      {showOnboarding && (
        <EmployeeOnboarding
          onClose={() => setShowOnboarding(false)}
          onComplete={loadEmployees}
        />
      )}

      <AddEmployeeDialog
        open={showAddEmployee}
        onClose={() => setShowAddEmployee(false)}
        onEmployeeAdded={loadEmployees}
        schools={schools}
        departments={departments}
      />
    </div>
  );
};

// Employee Grid View Component
const EmployeeGrid = ({ employees, selectedEmployees, onEmployeeSelect, onViewEmployee, canEdit }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {employees.map((employee) => (
        <Card key={employee.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              {canEdit && (
                <Checkbox
                  checked={selectedEmployees.includes(employee.id)}
                  onCheckedChange={(checked) => onEmployeeSelect(employee.id, checked)}
                />
              )}
              <Badge className={`${
                employee.status === 'active' ? 'bg-green-100 text-green-800' :
                employee.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {employee.status?.toUpperCase() || 'UNKNOWN'}
              </Badge>
            </div>
            
            <div className="text-center mb-4">
              <Avatar className="w-16 h-16 mx-auto mb-2">
                <AvatarImage src={employee.photo_url} alt={employee.display_name} />
                <AvatarFallback className="bg-gray-200">
                  {employee.first_name?.[0]}{employee.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <h3 className="font-semibold text-gray-900 truncate">
                {employee.first_name} {employee.last_name}
              </h3>
              <p className="text-sm text-gray-600 truncate">{employee.position_title || 'Staff Member'}</p>
            </div>

            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex items-center">
                <Mail className="w-3 h-3 mr-2" />
                <span className="truncate">{employee.email}</span>
              </div>
              <div className="flex items-center">
                <Building2 className="w-3 h-3 mr-2" />
                <span className="truncate">{employee.school_name || 'Unassigned'}</span>
              </div>
              {employee.hire_date && (
                <div className="flex items-center">
                  <Calendar className="w-3 h-3 mr-2" />
                  <span>Hired {new Date(employee.hire_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            <Button
              onClick={() => onViewEmployee(employee.id)}
              className="w-full mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
              size="sm"
            >
              <Eye className="w-3 h-3 mr-2" />
              View Profile
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Employee List View Component
const EmployeeList = ({ employees, selectedEmployees, onEmployeeSelect, onViewEmployee, canEdit }) => {
  return (
    <div className="space-y-2">
      {employees.map((employee) => (
        <Card key={employee.id} className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              {canEdit && (
                <Checkbox
                  checked={selectedEmployees.includes(employee.id)}
                  onCheckedChange={(checked) => onEmployeeSelect(employee.id, checked)}
                />
              )}
              
              <Avatar className="w-12 h-12">
                <AvatarImage src={employee.photo_url} alt={employee.display_name} />
                <AvatarFallback className="bg-gray-200">
                  {employee.first_name?.[0]}{employee.last_name?.[0]}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {employee.first_name} {employee.last_name}
                  </h3>
                  <Badge className={`${
                    employee.status === 'active' ? 'bg-green-100 text-green-800' :
                    employee.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {employee.status?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">{employee.position_title || 'Staff Member'}</p>
                <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                  <span className="flex items-center">
                    <Mail className="w-3 h-3 mr-1" />
                    {employee.email}
                  </span>
                  <span className="flex items-center">
                    <Building2 className="w-3 h-3 mr-1" />
                    {employee.school_name || 'Unassigned'}
                  </span>
                  {employee.hire_date && (
                    <span className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {new Date(employee.hire_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              <Button
                onClick={() => onViewEmployee(employee.id)}
                variant="outline"
                size="sm"
              >
                <Eye className="w-4 h-4 mr-2" />
                View
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Employee Table View Component
const EmployeeTable = ({ employees, selectedEmployees, onEmployeeSelect, onSelectAll, onViewEmployee, canEdit }) => {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                {canEdit && (
                  <th className="px-4 py-3 text-left">
                    <Checkbox
                      checked={selectedEmployees.length === employees.length && employees.length > 0}
                      onCheckedChange={onSelectAll}
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Employee</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Position</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">School</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Hire Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  {canEdit && (
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedEmployees.includes(employee.id)}
                        onCheckedChange={(checked) => onEmployeeSelect(employee.id, checked)}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={employee.photo_url} alt={employee.display_name} />
                        <AvatarFallback className="bg-gray-200 text-xs">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900">
                          {employee.first_name} {employee.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{employee.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {employee.position_title || 'Staff Member'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {employee.school_name || 'Unassigned'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`${
                      employee.status === 'active' ? 'bg-green-100 text-green-800' :
                      employee.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {employee.status?.toUpperCase() || 'UNKNOWN'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      onClick={() => onViewEmployee(employee.id)}
                      variant="outline"
                      size="sm"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Bulk Actions Dialog Component
const BulkActionsDialog = ({ open, onClose, selectedCount, onAction, schools, departments }) => {
  const [actionType, setActionType] = useState('');
  const [actionData, setActionData] = useState({});

  const handleAction = () => {
    if (!actionType) return;
    onAction(actionType, actionData);
    setActionType('');
    setActionData({});
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Actions ({selectedCount} employees)</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Action</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger>
                <SelectValue placeholder="Select action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="update_school">Update School</SelectItem>
                <SelectItem value="update_department">Update Department</SelectItem>
                <SelectItem value="update_status">Update Status</SelectItem>
                <SelectItem value="add_tags">Add Tags</SelectItem>
                <SelectItem value="remove_tags">Remove Tags</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {actionType === 'update_school' && (
            <div>
              <Label>School</Label>
              <Select 
                value={actionData.school_id || ''} 
                onValueChange={(value) => {
                  const school = schools.find(s => getSchoolKey(s) === value);
                  setActionData({ school_id: value, school_name: school?.name });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.map((school) => (
                    <SelectItem key={school.id} value={getSchoolKey(school)}>
                      {school.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {actionType === 'update_department' && (
            <div>
              <Label>Department</Label>
              <Select 
                value={actionData.department || ''} 
                onValueChange={(value) => setActionData({ department: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {actionType === 'update_status' && (
            <div>
              <Label>Status</Label>
              <Select 
                value={actionData.status || ''} 
                onValueChange={(value) => setActionData({ status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={!actionType}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              Apply Action
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};



// Add Employee Dialog Component
const AddEmployeeDialog = ({ open, onClose, onEmployeeAdded, schools, departments }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    position_title: '',
    department: '',
    school_id: '',
    hire_date: new Date().toISOString().split('T')[0],
    fte: 1.0
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      await apiService.makeRequest('/api/employees/', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      toast({
        title: 'Success',
        description: 'Employee created successfully.'
      });
      
      onClose();
      onEmployeeAdded();
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        position_title: '',
        department: '',
        school_id: '',
        hire_date: new Date().toISOString().split('T')[0],
        fte: 1.0
      });
    } catch (error) {
      console.error('Failed to create employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to create employee.',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Employee</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Position Title</Label>
              <Input
                value={formData.position_title}
                onChange={(e) => setFormData(prev => ({ ...prev, position_title: e.target.value }))}
              />
            </div>
            <div>
              <Label>Department</Label>
              <Select 
                value={formData.department} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>School</Label>
              <Select 
                value={formData.school_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, school_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.map((school) => (
                    <SelectItem key={school.id} value={getSchoolKey(school)}>
                      {school.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hire Date</Label>
              <Input
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData(prev => ({ ...prev, hire_date: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label>FTE</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={formData.fte}
              onChange={(e) => setFormData(prev => ({ ...prev, fte: parseFloat(e.target.value) }))}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={creating}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {creating ? 'Creating...' : 'Create Employee'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeManagement;